import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { api } from "../lib/api";
import { isProfileComplete } from "../lib/profile";
import { Button, ErrorBlock, Skeleton } from "./Ui";

export function EmailVerifiedGuard({ children }: { children: React.ReactNode }) {
	const { isLoaded, isSignedIn, session } = useAuth();
	if (!isLoaded) {
		return (
			<div style={{ maxWidth: 320, margin: "0 auto", display: "grid", gap: "0.5rem", padding: "1.5rem 0" }}>
				<Skeleton width="60%" height="0.9rem" />
				<Skeleton width="90%" height="0.9rem" />
				<Skeleton width="75%" height="0.9rem" />
			</div>
		);
	}
	if (!isSignedIn) return <>{children}</>;
	const emailVerified = session?.data?.user?.emailVerified;
	if (emailVerified === false) {
		return <Navigate to="/verify-email" replace />;
	}
	return <>{children}</>;
}

export function AdminRedirect({ children }: { children: React.ReactNode }) {
	const { isLoaded, isSignedIn, session } = useAuth();
	if (!isLoaded) return null;
	if (isSignedIn && session?.data?.user?.role === "admin" && !window.location.pathname.startsWith("/manage")) {
		return <Navigate to="/manage" replace />;
	}
	return <>{children}</>;
}

export function RequireAdmin({ children }: { children: React.ReactNode }) {
	const { isLoaded, isSignedIn, session } = useAuth();
	if (!isLoaded) {
		return (
			<div style={{ maxWidth: 320, margin: "0 auto", display: "grid", gap: "0.5rem", padding: "1.5rem 0" }}>
				<Skeleton width="60%" height="0.9rem" />
				<Skeleton width="90%" height="0.9rem" />
				<Skeleton width="75%" height="0.9rem" />
			</div>
		);
	}
	if (!isSignedIn) return <Navigate to="/login" replace />;
	if (session?.data?.user?.role !== "admin") {
		return <Navigate to="/dashboard" replace />;
	}
	return <>{children}</>;
}

export function RequireCouple({ children }: { children: React.ReactNode }) {
	const { getToken, isSignedIn, isLoaded } = useAuth();
	const [state, setState] = useState<
		| { kind: "loading" }
		| { kind: "ok" }
		| { kind: "redirect" }
		| { kind: "error"; message: string }
	>({ kind: "loading" });

	if (isLoaded && !isSignedIn && state.kind !== "redirect") {
		setState({ kind: "redirect" });
	}

	useEffect(() => {
		if (!isLoaded) return;
		if (!isSignedIn) return;
		let cancelled = false;
		(async () => {
			try {
				const token = await getToken();
				if (cancelled) return;
				if (!token) {
					setState({ kind: "redirect" });
					return;
				}
				const me = await api.me(token);
				if (cancelled) return;
				const profile = me.profile;
				const profileComplete = isProfileComplete(profile);
				if (!me.couple || !me.couple.paired || !profileComplete) {
					setState({ kind: "redirect" });
				} else {
					setState({ kind: "ok" });
				}
			} catch (err) {
				if (cancelled) return;
				setState({
					kind: "error",
					message: err instanceof Error ? err.message : "Tidak bisa memverifikasi status.",
				});
			}
		})();
		return () => { cancelled = true; };
	}, [isLoaded, isSignedIn, getToken]);

	if (state.kind === "loading") {
		return (
			<div style={{ maxWidth: 320, margin: "0 auto", display: "grid", gap: "0.5rem", padding: "1.5rem 0" }}>
				<Skeleton width="60%" height="0.9rem" />
				<Skeleton width="90%" height="0.9rem" />
				<Skeleton width="75%" height="0.9rem" />
			</div>
		);
	}
	if (state.kind === "error") {
		return (
			<div style={{ maxWidth: 320, margin: "0 auto", display: "grid", gap: "0.75rem", padding: "1.5rem 0" }}>
				<ErrorBlock message={state.message} />
				<Button variant="secondary" onClick={() => setState({ kind: "loading" })}>
					Coba lagi
				</Button>
			</div>
		);
	}
	if (state.kind === "redirect") {
		return <Navigate to="/dashboard" replace />;
	}
	return <>{children}</>;
}

export function RequireProfile({ children }: { children: React.ReactNode }) {
	const { getToken, isSignedIn, isLoaded } = useAuth();
	const [state, setState] = useState<
		| { kind: "loading" }
		| { kind: "ok" }
		| { kind: "redirect" }
		| { kind: "error"; message: string }
	>({ kind: "loading" });

	if (isLoaded && !isSignedIn && state.kind !== "redirect") {
		setState({ kind: "redirect" });
	}

	useEffect(() => {
		if (!isLoaded) return;
		if (!isSignedIn) return;
		let cancelled = false;
		(async () => {
			try {
				const token = await getToken();
				if (cancelled) return;
				if (!token) {
					setState({ kind: "redirect" });
					return;
				}
				const me = await api.me(token);
				if (cancelled) return;
				const profileComplete = isProfileComplete(me.profile);
				if (!profileComplete) {
					setState({ kind: "redirect" });
				} else {
					setState({ kind: "ok" });
				}
			} catch (err) {
				if (cancelled) return;
				setState({
					kind: "error",
					message: err instanceof Error ? err.message : "Tidak bisa memverifikasi profil.",
				});
			}
		})();
		return () => { cancelled = true; };
	}, [isLoaded, isSignedIn, getToken]);

	if (state.kind === "loading") {
		return (
			<div style={{ maxWidth: 320, margin: "0 auto", display: "grid", gap: "0.5rem", padding: "1.5rem 0" }}>
				<Skeleton width="60%" height="0.9rem" />
				<Skeleton width="90%" height="0.9rem" />
				<Skeleton width="75%" height="0.9rem" />
			</div>
		);
	}
	if (state.kind === "error") {
		return (
			<div style={{ maxWidth: 320, margin: "0 auto", display: "grid", gap: "0.75rem", padding: "1.5rem 0" }}>
				<ErrorBlock message={state.message} />
				<Button variant="secondary" onClick={() => setState({ kind: "loading" })}>
					Coba lagi
				</Button>
			</div>
		);
	}
	if (state.kind === "redirect") {
		return <Navigate to="/onboarding" replace />;
	}
	return <>{children}</>;
}
